/* eslint max-lines-per-function: ["error", {max: 265}]  -- Long function due to complex logic */
/* eslint max-lines: ["error", {max: 320}] -- Due to long file */
/* eslint max-statements: ["error", {max: 30}] -- Due to long function */
/* eslint complexity: ["error", {max: 31}] -- Due to long file */
import { useEffect, useState } from "react";

import every from "lodash/every";
import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import {
  EditEventAnalyticsEvent,
  ScheduleAnalyticsPageView,
  ScheduleAnalyticsSources,
} from "@/constants/analytics/Scheduling";
import { DateFormat } from "@/constants/DateFormat";
import {
  RecurrenceValue,
  ScheduleEventDetailsSubDeepLinks,
} from "@/constants/Schedule";
import { ScheduleTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useGroupDetailsErrorHandler from "@/hooks/useGroupDetailsErrorHandler";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetGroupDetailsQuery } from "@/store/api/schedule/groupClasses";
import { useAppSelector } from "@/store/hooks";
import { useBookingDispatch } from "@/store/reducers/booking";
import { selectScheduleEventDetailsData } from "@/store/reducers/modals/scheduleEventDetailsModal";
import GroupDetailsV2 from "@/ui/components/groupClasses/series/details/GroupDetailsV2";
import EditAppointmentBottomSheetModal from "@/ui/components/schedule/bottomSheetModals/EditAppointmentBottomSheetModal";
import EditGroupEventBottomSheetModal from "@/ui/components/schedule/bottomSheetModals/EditGroupEventBottomSheetModal";
import DeclineGroupSectionEventConfirmationAlert from "@/ui/components/schedule/confirmationAlerts/DeclineGroupSectionEventConfirmationAlert";
import EventDetails from "@/ui/components/schedule/details/EventDetails";
import { AddToCalendar } from "@/ui/components/schedule/details/EventDetailsActions";
import JoinZoomButton from "@/ui/components/schedule/JoinZoomButton";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { FullScreenSpinner } from "@/ui/core";
import { HStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { formatISODate } from "@/utils/date.util";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = ScheduleTestID.ScheduleEventDetails;

const Modal = () => {
  const { trackPageView } = useAnalytics();
  const { isAddToCalendarEnabled, isGroupClassRedesignEnabled } =
    useFeatureFlags();

  const modalDetails = useAppSelector(selectScheduleEventDetailsData);
  const { captureMessage } = useAppMonitor();

  const { setReschedulingAppointmentData } = useBookingDispatch();

  const {
    appointmentMetaData,
    eventDetails,
    isUpcomingEvent,
    selectedUserId,
    title,
  } = modalDetails || {};

  const {
    attendees,
    description,
    endDate,
    externalId,
    frequency,
    groupClassExternalId,
    groupSectionId,
    iconName,
    isGroupClassEvent,
    isPhoneCallEvent,
    isRecurring,
    patientExternalId,
    startDate,
    title: eventTitle,
  } = eventDetails || {};

  const [
    isEditGroupRegistrationModalOpen,
    setIsEditGroupRegistrationModalOpen,
  ] = useState<boolean>(false);

  const [isEditAppointmentModalOpen, setIsEditAppointmentModalOpen] =
    useState<boolean>(false);

  const [isDeclineEventAlertOpen, setIsDeclineEventAlertOpen] =
    useState<boolean>(false);

  // v2 group event details reuse the group details screen (fetched by group id) with a
  // decline-event action; group events adapt groupClassExternalId to the v2 group id
  const shouldUseV2GroupDetails =
    isGroupClassRedesignEnabled && Boolean(isGroupClassEvent);
  const patientIdForQuery = useScheduleQueryPatientId(patientExternalId ?? "");
  const {
    data: groupDetails,
    error: groupDetailsError,
    isError: isGroupDetailsError,
    isLoading: isGroupDetailsLoading,
  } = useGetGroupDetailsQuery(
    {
      groupId: groupClassExternalId ?? "",
      patientExternalId: patientIdForQuery,
    },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
      skip: !shouldUseV2GroupDetails || isNil(groupClassExternalId),
    },
  );

  useGroupDetailsErrorHandler({
    error: groupDetailsError,
    groupId: groupClassExternalId,
    isError: isGroupDetailsError,
    onError: () => router.dismiss(),
    patientId: patientIdForQuery,
  });

  useEffect(() => {
    const prefix = isUpcomingEvent ? "Upcoming " : "";
    const category = isGroupClassEvent
      ? ScheduleAnalyticsPageView.GroupEventDetailView
      : ScheduleAnalyticsPageView.AppointmentDetailView;

    trackPageView(`${prefix}${category} - ${eventTitle}`);
  }, [trackPageView, eventTitle, isGroupClassEvent, isUpcomingEvent]);

  const handleEditAppointmentPress = () => {
    if (isNil(appointmentMetaData)) {
      captureMessage("No event metadata found for appointment.", {
        context: {
          eventTitle,
          externalId,
        },
      });
      return;
    }

    setReschedulingAppointmentData(appointmentMetaData);
    setIsEditAppointmentModalOpen(true);
  };

  const handleEditEventPress = () => {
    if (isGroupClassEvent) {
      setIsEditGroupRegistrationModalOpen(true);
    } else {
      handleEditAppointmentPress();
    }
  };

  // Handle the in-app `subtarget=edit` sub-deep link by acting as if the user
  // pressed the edit button once the appointment data has loaded.
  useSubDeepLink({
    analyticsEvent: EditEventAnalyticsEvent.EditAppointmentPressed,
    analyticsProps: {
      component: AnalyticsComponent.Button,
      source: ScheduleAnalyticsSources.AppointmentDetailView,
    },
    onOpen: handleEditEventPress,
    paramName: "subtarget",
    shouldHandle: !isGroupClassEvent && !isNil(appointmentMetaData),
    targetValue: ScheduleEventDetailsSubDeepLinks.Edit,
  });

  if (
    isNil(eventTitle) ||
    isNil(externalId) ||
    isNil(startDate) ||
    isNil(endDate) ||
    isNil(attendees) ||
    !every([eventTitle, externalId, startDate, endDate, attendees])
  ) {
    if (isWeb()) {
      router.dismiss();
    }
    return <FullScreenSpinner />;
  }

  const actionComponents = (
    <HStack>
      {isAddToCalendarEnabled && (
        <AddToCalendar
          event={{
            endDate: new Date(endDate),
            startDate: new Date(startDate),
            title: eventTitle,
          }}
          eventExternalId={externalId}
          isGroupClassEvent={isGroupClassEvent ?? false}
        />
      )}
    </HStack>
  );

  const v2GroupDetails =
    isGroupDetailsLoading || isNil(groupDetails) ? (
      <FullScreenSpinner />
    ) : (
      <GroupDetailsV2
        eventDateString={
          startDate
            ? formatISODate({
                dateString: startDate,
                format: DateFormat.weekDayMonthDay,
              })
            : undefined
        }
        group={groupDetails}
        groupSectionId={groupSectionId}
        onDeclineEventPress={() => setIsDeclineEventAlertOpen(true)}
        patientId={patientExternalId ?? selectedUserId ?? ""}
        source={GroupClassSeriesAnalyticsSources.ScheduleEvent}
      />
    );

  const shouldRenderEditAppointmentModal =
    !isNil(appointmentMetaData) && !isGroupClassEvent;
  const shouldRenderEditGroupRegistrationModal =
    isNotNilNotEmpty(groupClassExternalId) &&
    isGroupClassEvent &&
    isNotNilNotEmpty(patientExternalId);

  return (
    <ModalLayout
      isPaddedHorizontally
      renderFloatingButton={() => {
        return (
          <JoinZoomButton
            endDate={endDate}
            eventId={externalId}
            eventTitle={eventTitle}
            hasIcon
            isFloating
            isGroupClass={isGroupClassEvent}
            isPhoneCallEvent={isPhoneCallEvent}
            location="details"
            patientId={patientExternalId ?? selectedUserId}
            selectedUserId={selectedUserId}
            size="normal"
            startDate={startDate}
          />
        );
      }}
      renderHeader={() => {
        return (
          <ModalTitleHeader
            actionComponents={actionComponents}
            onDismiss={() => router.dismiss()}
            testID={joinTestIDs(baseTestID, "title")}
            title={title || ""}
          />
        );
      }}
    >
      {shouldUseV2GroupDetails ? (
        v2GroupDetails
      ) : (
        <EventDetails
          attendees={attendees}
          description={description}
          endDate={endDate}
          externalId={externalId}
          frequency={frequency || RecurrenceValue.DoesNotRepeat}
          iconName={iconName}
          isGroupClassEvent={isGroupClassEvent}
          isRecurring={isRecurring}
          onEditEventPress={handleEditEventPress}
          startDate={startDate}
          title={eventTitle}
        />
      )}
      {shouldUseV2GroupDetails && (
        <DeclineGroupSectionEventConfirmationAlert
          eventTitle={eventTitle}
          groupSectionEventId={externalId}
          isOpen={isDeclineEventAlertOpen}
          onCompletion={() => {
            setIsDeclineEventAlertOpen(false);
            router.dismiss();
          }}
          onDismiss={() => setIsDeclineEventAlertOpen(false)}
          patientExternalId={patientExternalId}
        />
      )}
      {!shouldUseV2GroupDetails && shouldRenderEditGroupRegistrationModal && (
        <EditGroupEventBottomSheetModal
          eventExternalId={externalId}
          eventTitle={eventTitle}
          groupClassSeriesExternalId={groupClassExternalId}
          isOpen={isEditGroupRegistrationModalOpen}
          onClose={() => setIsEditGroupRegistrationModalOpen(false)}
          onCompletion={() => router.dismiss()}
          patientExternalId={patientExternalId}
        />
      )}

      {shouldRenderEditAppointmentModal && (
        <EditAppointmentBottomSheetModal
          eventMetadata={appointmentMetaData}
          eventTitle={eventTitle}
          isOpen={isEditAppointmentModalOpen}
          onClose={() => setIsEditAppointmentModalOpen(false)}
          onCompletion={() => router.dismiss()}
        />
      )}
    </ModalLayout>
  );
};

export default Modal;
